import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Globe, TestTube, RefreshCw, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  detectBrowserLanguage, 
  detectBrowserCityLanguage, 
  getStoredLanguagePreference,
  getBestLanguageMatch
} from '@/utils/browserLanguageDetector';
import { extractLanguageFromPath, getCanonicalPath } from '@/utils/routing/multilingualRouting';
import { runMultilingualDebugSuite, getLanguageCoverageReport } from '@/utils/multilingualDebugger';

export const LanguageTestPanel = () => {
  const { language, changeLanguageAndNavigate } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [testResults, setTestResults] = useState<any>(null);

  const runLanguageTests = () => {
    const results = {
      currentUrl: location.pathname,
      extractedLanguage: extractLanguageFromPath(location.pathname),
      canonicalPath: getCanonicalPath(location.pathname),
      browserLanguage: detectBrowserLanguage(),
      browserCityLanguage: detectBrowserCityLanguage(),
      storedPreference: getStoredLanguagePreference(),
      bestMatch: getBestLanguageMatch(extractLanguageFromPath(location.pathname)),
      currentContext: language,
      timestamp: new Date().toISOString()
    };

    setTestResults(results);
    console.log('🧪 Language Test Results:', results);
  };

  const runFullDebugSuite = () => {
    runMultilingualDebugSuite();
    const coverage = getLanguageCoverageReport();
    console.log('📊 Language Coverage:', coverage);
  };

  const testLanguageNavigation = (testLang: any) => {
    changeLanguageAndNavigate(testLang);
  };

  const testPaths = [
    '/city/madrid',
    '/es/city/madrid',
    '/city/paris', 
    '/fr/city/paris',
    '/city/london',
    '/city/tokyo',
    '/ja/city/tokyo'
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5" />
            Language System Test Panel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current State */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Current Language</div>
              <Badge variant="default">{language}</Badge>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Current Path</div>
              <div className="text-sm font-mono">{location.pathname}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">URL Language</div>
              <Badge variant="outline">
                {extractLanguageFromPath(location.pathname) || 'none'}
              </Badge>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Canonical</div>
              <div className="text-sm font-mono">{getCanonicalPath(location.pathname)}</div>
            </div>
          </div>

          {/* Test Actions */}
          <div className="flex flex-wrap gap-2">
            <Button onClick={runLanguageTests} variant="outline" size="sm">
              <TestTube className="h-4 w-4 mr-2" />
              Run Tests
            </Button>
            <Button onClick={runFullDebugSuite} variant="outline" size="sm">
              <Globe className="h-4 w-4 mr-2" />
              Full Debug Suite
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload Page
            </Button>
          </div>

          {/* Quick Language Tests */}
          <div>
            <div className="text-sm font-medium mb-2">Quick Language Navigation Tests:</div>
            <div className="flex flex-wrap gap-2">
              {(['en', 'es', 'fr', 'de'] as const).map(lang => (
                <Button 
                  key={lang}
                  onClick={() => testLanguageNavigation(lang)}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          {/* Test Results */}
          {testResults && (
            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Test Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <strong>Browser Language:</strong> {testResults.browserLanguage}
                    </div>
                    <div>
                      <strong>Browser City Language:</strong> {testResults.browserCityLanguage}
                    </div>
                    <div>
                      <strong>Stored Preference:</strong> {testResults.storedPreference}
                    </div>
                    <div>
                      <strong>Best Match:</strong> {testResults.bestMatch}
                    </div>
                    <div>
                      <strong>Context Language:</strong> {testResults.currentContext}
                    </div>
                    <div>
                      <strong>Extracted from URL:</strong> {testResults.extractedLanguage || 'none'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Test Paths */}
          <div>
            <div className="text-sm font-medium mb-2">Test Navigation Paths:</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {testPaths.map(path => (
                <Button 
                  key={path}
                  onClick={() => navigate(path)}
                  variant="outline"
                  size="sm"
                  className="justify-start text-left font-mono"
                >
                  {path}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};